import { errorMessage, successMessage } from "./notificationUtils";
import type { PortalClaimValidationScheme } from "../components/patient/Portal Registration/PortalClaimValidationModal";
import { claimValidationHealthIndia, updateClaimValidationHealthIndia } from "../apis/SchemeService/HEALTHINDIA/healthIndiaService";
import { claimValidationHeritage, updateClaimValidationHeritage } from "../apis/SchemeService/HERITAGE/heritageService";
import { claimValidationFhpl, updateClaimValidationFhpl } from "../apis/SchemeService/FHPL/fhplService";
import { claimValidationStarHealth, updateClaimValidationStarHealth } from "../apis/SchemeService/STARHEALTH/starHealthService";
import { resolveHeritageHealthIndiaClaimId } from "./heritageHealthIndiaClaimId";

export const resolveClaimResponseDto = (validationRes: any) => {
  if (!validationRes) {
    return null;
  }

  const nested =
    validationRes?.data &&
    typeof validationRes.data === "object" &&
    !Array.isArray(validationRes.data)
      ? validationRes.data
      : null;

  const claimResponse =
    validationRes?.claimResponseDTO ??
    validationRes?.claimResponseDto ??
    nested?.claimResponseDTO ??
    nested?.claimResponseDto ??
    null;

  if (!claimResponse || typeof claimResponse !== "object") {
    return null;
  }

  return {
    ...claimResponse,
    claimId:
      claimResponse?.claimId ??
      claimResponse?.ccn ??
      claimResponse?.claimNumber ??
      null,
    patientName:
      claimResponse?.patientName ??
      claimResponse?.insuredName ??
      null,
    opNumber:
      claimResponse?.opNumber ??
      claimResponse?.inPatientNumber ??
      null,
  };
};

export const normalizeValidationResponse = (validationRes: any) => {
  if (!validationRes) {
    return null;
  }

  const merged =
    validationRes?.data &&
    typeof validationRes.data === "object" &&
    !Array.isArray(validationRes.data)
      ? { ...validationRes, ...validationRes.data }
      : validationRes;

  const claimResponseDTO = resolveClaimResponseDto(merged);

  return {
    ...merged,
    claimResponseDTO,
    status: merged?.status ?? merged?.validationStatus ?? null,
    discrepancyStatus: merged?.discrepancyStatus ?? null,
  };
};

type ValidateClaimParams = {
  scheme: PortalClaimValidationScheme;
  caseId?: number;
  visitId?: number;
  healthcareId?: number;
  schemeId?: number;
  claimId?: string | null;
  portalData?: any;
  patients?: any;
};

const resolveClaimId = ({
  scheme,
  claimId,
  portalData,
  patients,
}: ValidateClaimParams) => {
  if (claimId) {
    return claimId;
  }

  if (scheme === "STAR_HEALTH") {
    return (
      portalData?.schemeRegistrationId ||
      portalData?.casePreAuthStarhealth?.claimId ||
      null
    );
  }

  const healthIndiaData =
    portalData?.casePreAuthOthersResponse?.data ||
    portalData?.casePreAuthHealthIndia?.data;
  const heritageData =
    portalData?.casePreAuthHeritage?.data ||
    portalData?.casePreAuthOthersResponse?.data;
  const fhplData =
    portalData?.casePreAuthFhpl?.data ||
    portalData?.casePreAuthOthersResponse?.data;

  return (
    (scheme === "HERITAGE"
      ? heritageData?.claimId
      : scheme === "FHPL"
        ? fhplData?.claimId
        : healthIndiaData?.claimId) ||
    resolveHeritageHealthIndiaClaimId(portalData) ||
    patients?.fetchBasicDetails?.visitInfoResponse?.claimId ||
    null
  );
};

export const validatePortalClaim = async (params: ValidateClaimParams) => {
  const registrationId = resolveClaimId(params);
  if (!registrationId) {
    errorMessage("Claim ID is required for validation");
    return null;
  }

  const payload = {
    registrationId,
    caseId: params.caseId,
    visitId: params.visitId,
    healthcareId: params.healthcareId,
    schemeId: params.schemeId,
  };

  if (params.scheme === "HEALTH_INDIA") {
    return claimValidationHealthIndia(payload);
  }
  if (params.scheme === "HERITAGE") {
    return claimValidationHeritage(payload);
  }
  if (params.scheme === "FHPL") {
    return claimValidationFhpl(payload);
  }
  return claimValidationStarHealth(payload);
};

export const updatePortalClaimValidation = async (
  scheme: PortalClaimValidationScheme,
  caseId: any,
  visitId: any,
  data: any,
) => {
  if (scheme === "HEALTH_INDIA") {
    return updateClaimValidationHealthIndia(caseId, visitId, data);
  }
  if (scheme === "HERITAGE") {
    return updateClaimValidationHeritage(caseId, visitId, data);
  }
  if (scheme === "FHPL") {
    return updateClaimValidationFhpl(caseId, visitId, data);
  }
  return updateClaimValidationStarHealth(caseId, visitId, data);
};

export const handlePortalClaimValidationFlow = async ({
  scheme,
  caseId,
  visitId,
  healthcareId,
  schemeId,
  claimId,
  portalData,
  patients,
  setIsValidationLoading,
  setValidationRes,
  setRegistrationValidationStatus,
  setIsValidationModalOpen,
}: ValidateClaimParams & {
  setIsValidationLoading: (value: boolean) => void;
  setValidationRes: (value: any) => void;
  setRegistrationValidationStatus: (value: string) => void;
  setIsValidationModalOpen: (value: boolean) => void;
}) => {
  try {
    setIsValidationLoading(true);
    const res = await validatePortalClaim({
      scheme,
      caseId,
      visitId,
      healthcareId,
      schemeId,
      claimId,
      portalData,
      patients,
    });

    const normalizedRes = normalizeValidationResponse(res);
    setValidationRes(normalizedRes);
    const status =
      normalizedRes?.status ||
      normalizedRes?.data?.status ||
      normalizedRes?.validationStatus;

    if (
      status?.toLowerCase() === "validated" ||
      status?.toLowerCase() === "completed" ||
      status?.toLowerCase() === "failed"
    ) {
      successMessage("Claim validation completed!");
      if (
        status?.toLowerCase() === "validated" &&
        normalizedRes?.discrepancyStatus
      ) {
        setRegistrationValidationStatus("Discrepancy");
      } else {
        setRegistrationValidationStatus(status);
      }
      setIsValidationModalOpen(true);
      setIsValidationLoading(false);
      return normalizedRes;
    }

    errorMessage("Request timeout.");
    setRegistrationValidationStatus("Failed");
    setIsValidationLoading(false);
    return null;
  } catch (err) {
    setIsValidationLoading(false);
    errorMessage("Please, Try again after sometime");
    return null;
  }
};
